import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { FileText, Globe, Link, Zap, Filter, Eye, Code, List, type LucideIcon } from '../../../icons';
// settings-*/ai-tools-* 클래스 스타일 SSOT(8443 SettingsPage.css 와 동일) — admin 이식 시 누락분 복원.
import './SettingsSections.css';
// 답변 포맷·톤은 별도 메뉴(답변 포맷 / 답변 톤)로 분리. design-20260604-answer-tone-format-orthogonal.
// settings-*/ai-tools-* 를 svc 톤으로 재스킨(미러 무수정, .ai-svctone 스코프).
import './AiSvcTone.css';
// 상단 연결 서버 카드(DB/업무) — 신규 기능 격리 스타일.
import './AiToolsServers.css';
import { StyledSelect } from './MenuControls';
// 연결된 도구 카탈로그(도메인별 카드) — 신규 기능 격리 스타일.
import './AiToolsCatalog.css';
// 도구 생성기 모드 카드/단계 안내 — 신규 기능 격리 스타일.
import './AiToolsGenerator.css';
import {
  listAiTools,
  refreshAiTools,
  listAiToolConnections,
  createAiToolConnection,
  deleteAiToolConnection,
  testAiToolConnection,
  createCustomAiTool,
  deleteCustomAiTool,
  synthesizeAiTool,
  introspectConnection,
  synthesizeBatch,
  createCustomAiToolsBatch,
  openapiPreview,
  openapiSynthesize,
  openapiImport,
  type AiToolsListResult,
  type AiToolCacheInfo,
  type AiToolConnection,
  type AiToolConnectionKind,
  type AiToolMeta,
  type SynthesizeResult,
  type ConnectionTestResult,
  type DbSchema,
  type ToolCandidate,
  type OpenApiPreview,
} from '../../../api/aiTools';
// 카테고리/도구별 답변 톤·포맷 선택 — 답변 톤/포맷 메뉴와 동일 SSOT(synthesis-format.json) 공유.
import {
  useStyleConfig, FormatMock, FormatSample, FormatSelect, formatKind, formatStyleVars, StyleSaveBar,
  type StyleApi,
} from './AiAnswerStyle.shared';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

function toneLabelOf(style: StyleApi, k: string | undefined): string {
  if (!k) return '미설정';
  return style.fmt?.tones[k]?.description || k;
}
function fmtLabelOf(style: StyleApi, k: string | undefined): string {
  if (!k) return '미설정';
  return style.fmt?.formats?.[k]?.description || k;
}

/**
 * AiToolsSection — Settings 의 AI 도구 탭 (AI Provider 옆).
 *
 * 기능 (orchestrator-svc tools-admin 백엔드 중계):
 *   1. 조회 — 도메인별 도구 목록 + 캐시 stats + 새로고침.
 *   2. 도구 생성기(위저드) — 3 모드. 외부 연결 추가/선택/test/삭제가 각 모드에 인라인 통합됨:
 *        a. DB 분석   : 연결(sqlite/postgres/mysql) 스키마 분석 → AI 가 조회 도구 다량 생성 → 선택 저장.
 *        b. Swagger   : OpenAPI 명세 → 엔드포인트 도구 미리보기 → AI 설명 보강 → 선택 import (연결 자동 생성).
 *        c. 수동 brief: 연결(전체 종류) + 자연어 → 도구 1개 생성.
 *   3. 관리 — custom 도구 삭제.
 *
 * 권한: ADMIN/SYSTEM_ADMIN. 아이콘은 인라인 SVG (이모지 미사용).
 */
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export const CUSTOM_KINDS = new Set(['external_http', 'external_db']);

function errMsg(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string; message?: string; validation_error?: string; detail?: string } } }).response;
    return r?.data?.validation_error || r?.data?.error || r?.data?.detail || r?.data?.message || '요청 실패';
  }
  return e instanceof Error ? e.message : String(e);
}

// ─── 인라인 SVG 아이콘 ──────────────────────────────────────────
const IconDb = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" /><path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
  </svg>
);
const IconApi = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M18 10h1a3 3 0 0 1 0 6h-1" /><path d="M6 10H5a3 3 0 0 0 0 6h1" /><path d="M8 13h8" />
  </svg>
);
const IconWand = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M15 4V2M15 10V8M11 6H9M21 6h-2M18 9l-1.5-1.5M18 3l-1.5 1.5M4 20l10-10" />
  </svg>
);
const IconChevron = ({ open }: { open: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden
    style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
    <path d="M9 18l6-6-6-6" />
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);
// 업무 서버(HTTP/API) 카드용 — DB 와 구분되는 서버 아이콘.
const IconServer = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <rect x="3" y="4" width="18" height="7" rx="1.5" /><rect x="3" y="13" width="18" height="7" rx="1.5" />
    <path d="M7 7.5h.01M7 16.5h.01" />
  </svg>
);
const IconDbLarge = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v14c0 1.7 3.6 3 8 3s8-1.3 8-3V5" /><path d="M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
  </svg>
);
// 도구 카탈로그 도메인 헤더용 — 렌치(도구) 아이콘.
const IconTool = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-2.4 2.6-2.8z" />
  </svg>
);
const IconX = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const IconCheck = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const IconInfo = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
  </svg>
);

type GenMode = 'db' | 'swagger' | 'brief';
const DB_KINDS: AiToolConnectionKind[] = ['sqlite', 'postgres', 'mysql'];
const ALL_KINDS: AiToolConnectionKind[] = ['http', 'sqlite', 'postgres', 'mysql'];

// 생성기 모드 메타 — 카드 표시(아이콘/제목/설명) + 단계 안내(stepper).
interface GenModeDef {
  key: GenMode;
  title: string;
  icon: () => JSX.Element;
  accent: { c: string; t: string };
  desc: string;
  steps: string[];
}
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export const GEN_MODES: GenModeDef[] = [
  {
    key: 'db', title: 'DB 분석 생성', icon: IconDb,
    accent: { c: '#0ea5e9', t: 'rgba(14, 165, 233, 0.13)' },
    desc: 'DB를 연결하면 AI가 스키마를 읽어 조회 도구를 한 번에 생성합니다.',
    steps: ['연결 선택·추가', '스키마 분석', 'AI로 도구 생성', '후보 선택 후 저장'],
  },
  {
    key: 'swagger', title: 'Swagger 가져오기', icon: IconApi,
    accent: { c: '#f59e0b', t: 'rgba(245, 158, 11, 0.15)' },
    desc: 'OpenAPI 3.x 명세로 엔드포인트 도구를 미리보고 가져옵니다.',
    steps: ['connection id 입력', '명세 URL·본문', '미리보기·AI 보강', '선택 가져오기'],
  },
  {
    key: 'brief', title: '수동 brief', icon: IconWand,
    accent: { c: '#8b5cf6', t: 'rgba(139, 92, 246, 0.13)' },
    desc: '자연어 설명 한 줄로 도구 1개를 생성합니다.',
    steps: ['연결 선택', '도구 설명 입력', 'AI 생성', '검토 후 저장'],
  },
];

export function AiToolsSection() {
  const [data, setData] = useState<AiToolsListResult | null>(null);
  const [connections, setConnections] = useState<AiToolConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [genMode, setGenMode] = useState<GenMode>('db');
  const [selectedServer, setSelectedServer] = useState<ServerSel>(null);
  const style = useStyleConfig(); // 카테고리/도구별 답변 톤·포맷 (synthesis-format.json)

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tools, conns] = await Promise.all([listAiTools(), listAiToolConnections()]);
      setData(tools);
      setConnections(conns.connections ?? []);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const customTools = useMemo(
    () => (data?.tools ?? []).filter((t) => CUSTOM_KINDS.has(String(t._meta?.tool_kind ?? t.tool_kind ?? ''))),
    [data],
  );

  // 선택한 서버 카드의 도구만 필터(미선택 시 전체).
  const filteredTools = useMemo(() => filterToolsByServer(data?.tools ?? [], selectedServer), [data, selectedServer]);
  const filteredByDomain = useMemo(
    () => (selectedServer ? groupByDomain(filteredTools) : (data?.by_domain ?? {})),
    [selectedServer, filteredTools, data],
  );

  const onRefresh = async () => {
    setBusy('refresh');
    try {
      await refreshAiTools();
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const onDeleteCustom = async (name: string) => {
    if (!window.confirm(`도구 "${name}" 을(를) 삭제할까요?`)) return;
    setBusy(`del-${name}`);
    try {
      await deleteCustomAiTool(name);
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <p className="settings-muted">AI 도구 정보를 불러오는 중…</p>;
  if (error && !data) {
    return (
      <div className="settings-error" role="alert">
        AI 도구 조회 실패: {error}
        <button className="settings-btn" onClick={() => void load()} style={{ marginLeft: 8 }}>재시도</button>
      </div>
    );
  }

  return (
    <div className="ai-tools-section ai-svctone">
      <ConsoleScreenHead
        title="AI 도구"
        desc="오케스트레이터가 부를 수 있는 도구와 연결된 서버를 관리합니다 — 서버 전역 설정입니다."
      />
      {error && <div className="settings-error" role="alert">{error}</div>}

      {/* ── 0. 연결된 서버(DB/업무) 카드 ── */}
      <section className="ai-tools-block">
        <header className="ai-tools-block__head">
          <h3>연결된 서버</h3>
          <button className="settings-btn" disabled={busy === 'refresh'} onClick={() => void onRefresh()}>
            {busy === 'refresh' ? '새로고침 중…' : '새로고침'}
          </button>
        </header>
        <ServerCards connections={connections} tools={data?.tools ?? []}
          selected={selectedServer} onSelect={setSelectedServer} />
      </section>

      {/* ── 1. 연결된 도구 (답변 포맷·톤은 별도 메뉴로 분리) ── */}
      <section className="ai-tools-block">
        <header className="ai-tools-block__head">
          <h3>연결된 도구 ({selectedServer ? filteredTools.length : (data?.total ?? 0)})</h3>
          <button className="settings-btn" disabled={busy === 'refresh'} onClick={() => void onRefresh()}>
            {busy === 'refresh' ? '새로고침 중…' : '캐시 새로고침'}
          </button>
        </header>
        {selectedServer ? (
          <div className="ait-srv-filter" role="status">
            <span className="ait-srv-filter__label">
              <IconServer size={13} /> <b>{selectedServer.label}</b> 서버의 도구 {filteredTools.length}개
            </span>
            <button type="button" className="ait-srv-filter__clear" onClick={() => setSelectedServer(null)}>전체 보기</button>
          </div>
        ) : (
          <p className="ait-gen-intro">
            위 <b>연결된 서버 카드를 클릭</b>하면 해당 서버의 도구만 볼 수 있습니다. 채팅 답변의 <b>말투</b>는 “답변 톤”,
            <b>표시 형식</b>은 “답변 포맷” 메뉴에서 카테고리별로 설정합니다.
          </p>
        )}
        <ToolCatalog byDomain={filteredByDomain} cacheStats={data?.cache_stats ?? []} style={style} />
        {style.fmt && <StyleSaveBar api={style} label="답변 톤·포맷 저장" />}
      </section>

      {/* ── 2. 도구 생성기(위저드) ── */}
      <section className="ai-tools-block">
        <h3>도구 생성기</h3>
        <p className="ait-gen-intro">
          아래에서 등록 방식을 고르세요. 데이터 소스를 연결하면 AI 가 분석해 도구를 만들고, 저장 즉시 오케스트레이터에 연결됩니다. 채팅에서 관련 키워드를 말하면 자동으로 호출됩니다.
        </p>

        {/* 모드 선택 카드 */}
        <div className="ait-gen-modes" role="tablist" aria-label="도구 등록 방식">
          {GEN_MODES.map((m) => {
            const Icon = m.icon;
            const active = genMode === m.key;
            return (
              <button
                key={m.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`ait-gen-mode${active ? ' is-active' : ''}`}
                style={{ ['--cat' as string]: m.accent.c, ['--cat-tint' as string]: m.accent.t }}
                onClick={() => setGenMode(m.key)}
              >
                <span className="ait-gen-mode__top">
                  <span className="ait-gen-mode__icon"><Icon /></span>
                  <span className="ait-gen-mode__title">{m.title}</span>
                  <span className="ait-gen-mode__check" aria-hidden><IconCheck /></span>
                </span>
                <span className="ait-gen-mode__desc">{m.desc}</span>
              </button>
            );
          })}
        </div>

        {/* 단계 안내(stepper) — 선택 모드 기준 */}
        {(() => {
          const m = GEN_MODES.find((x) => x.key === genMode)!;
          return (
            <div className="ait-gen-guide" style={{ ['--cat' as string]: m.accent.c, ['--cat-tint' as string]: m.accent.t }}>
              <span className="ait-gen-guide__lead"><IconInfo /> 등록 단계</span>
              {m.steps.map((s, i) => (
                <span key={s} className="ait-gen-step">
                  {i > 0 && <span className="ait-gen-step__arrow" aria-hidden>→</span>}
                  <span className="ait-gen-step__num">{i + 1}</span>
                  <span className="ait-gen-step__label">{s}</span>
                </span>
              ))}
            </div>
          );
        })()}

        {/* 모드 패널 — key 로 전환 시 재진입 애니메이션 */}
        <div className="ait-gen-panel" key={genMode}>
          {genMode === 'db' && <DbGenerator connections={connections} reload={load} />}
          {genMode === 'swagger' && <SwaggerImporter onSaved={load} />}
          {genMode === 'brief' && <BriefGenerator connections={connections} reload={load} />}
        </div>
      </section>

      {/* ── 3. 관리 — custom 도구 ── */}
      <section className="ai-tools-block">
        <h3>사용자 정의 도구 ({customTools.length})</h3>
        {customTools.length === 0 ? (
          <p className="settings-muted">사용자 정의(custom) 도구가 없습니다.</p>
        ) : (
          <ul className="ai-tools-list">
            {customTools.map((t) => (
              <li key={t.name} className="ai-tools-item ai-tools-item--manage">
                <div>
                  <code className="ai-tools-item__name">{t.name}</code>
                  <p className="ai-tools-item__desc">{t.description}</p>
                </div>
                <button className="settings-btn settings-btn--danger" disabled={busy === `del-${t.name}`} onClick={() => void onDeleteCustom(t.name)}>
                  삭제
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  연결된 도구 카탈로그 — 도메인(카테고리)별 패널 + 도구 카드 그리드
// ════════════════════════════════════════════════════════════════

// 카테고리 액센트 팔레트 — 도메인별 고유 색으로 시각 구분.
const CAT_PALETTE: { c: string; t: string }[] = [
  { c: '#6366f1', t: 'rgba(99, 102, 241, 0.13)' },  // indigo
  { c: '#0ea5e9', t: 'rgba(14, 165, 233, 0.13)' },  // sky
  { c: '#f59e0b', t: 'rgba(245, 158, 11, 0.15)' },  // amber
  { c: '#10b981', t: 'rgba(16, 185, 129, 0.13)' },  // emerald
  { c: '#ec4899', t: 'rgba(236, 72, 153, 0.13)' },  // pink
  { c: '#8b5cf6', t: 'rgba(139, 92, 246, 0.13)' },  // violet
  { c: '#14b8a6', t: 'rgba(20, 184, 166, 0.13)' },  // teal
];
// 알려진 도메인은 고정 색(서버 카드와 일관). 그 외는 이름 해시로 안정 배정.
const FIXED_DOMAIN_IDX: Record<string, number> = { chat: 0, hr: 1, calendar: 2, wiki: 3 };
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function accentForDomain(domain: string): { c: string; t: string } {
  if (domain in FIXED_DOMAIN_IDX) return CAT_PALETTE[FIXED_DOMAIN_IDX[domain]!]!;
  let h = 0;
  for (let i = 0; i < domain.length; i++) h = (h * 31 + domain.charCodeAt(i)) >>> 0;
  return CAT_PALETTE[h % CAT_PALETTE.length]!;
}
const DOMAIN_LABELS: Record<string, string> = {
  chat: '채팅', hr: '인사', calendar: '캘린더', wiki: '위키', static: '기본',
};
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function fmtAge(ms: number | null | undefined): string {
  if (ms == null) return '미로드';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s 전`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m}분 전` : `${Math.round(m / 60)}시간 전`;
}

function ToolCatalog({
  byDomain, cacheStats, style,
}: {
  byDomain: Record<string, AiToolMeta[]>;
  cacheStats: AiToolCacheInfo[];
  style: StyleApi;
}) {
  const cacheByDomain = useMemo(() => {
    const m: Record<string, AiToolCacheInfo> = {};
    for (const c of cacheStats) m[c.domain] = c;
    return m;
  }, [cacheStats]);

  // 클릭한 도구의 상세 레이어 팝업 — 도구 + 액센트 보관.
  const [selected, setSelected] = useState<{ tool: AiToolMeta; accent: { c: string; t: string }; domain: string } | null>(null);

  const domains = Object.entries(byDomain);
  if (domains.length === 0) {
    return <p className="settings-muted">등록된 도구가 없습니다.</p>;
  }

  return (
    <>
      {cacheStats.length > 0 && (
        <div className="ait-cat-cache">
          {cacheStats.map((c) => (
            <span key={c.domain} className="ait-cat-cache__chip">
              <span className={`ait-cat-cache__dot${c.age_ms == null ? ' ait-cat-cache__dot--stale' : ''}`} />
              {DOMAIN_LABELS[c.domain] ?? c.domain} {c.size}개 · {fmtAge(c.age_ms)}
            </span>
          ))}
        </div>
      )}
      <div className="ait-cat">
        {domains.map(([domain, tools]) => {
          const a = accentForDomain(domain);
          const age = cacheByDomain[domain]?.age_ms;
          return (
            <section
              key={domain}
              className="ait-cat-group"
              style={{ ['--cat' as string]: a.c, ['--cat-tint' as string]: a.t }}
            >
              <header className="ait-cat-group__head">
                <span className="ait-cat-group__icon"><IconTool /></span>
                <span className="ait-cat-group__name">{DOMAIN_LABELS[domain] ?? domain}</span>
                <span className="ait-cat-group__count">{tools.length}</span>
                <span className="ait-cat-group__age">{age !== undefined ? fmtAge(age) : ''}</span>
              </header>
              <div className="ait-cat-group__body">
                {style.fmt && <CategoryStyleRow domain={domain} style={style} />}
                <div className="ait-cat-grid">
                  {tools.map((t) => (
                    <button
                      type="button"
                      key={t.name}
                      className="ait-cat-tool"
                      onClick={() => setSelected({ tool: t, accent: a, domain })}
                      aria-haspopup="dialog"
                    >
                      <div className="ait-cat-tool__top">
                        <code className="ait-cat-tool__name" title={t.name}>{t.name}</code>
                      </div>
                      <div className="ait-cat-tool__badges">
                        {t.required_role && <span className="ai-tools-badge">{t.required_role}</span>}
                        {t.trust_class && <span className={`ai-tools-badge ai-tools-badge--${t.trust_class}`}>{t.trust_class}</span>}
                        {(t._meta?.tool_kind || t.tool_kind) && (
                          <span className="ai-tools-badge ai-tools-badge--kind">{String(t._meta?.tool_kind ?? t.tool_kind)}</span>
                        )}
                      </div>
                      <p className="ait-cat-tool__desc">{t.description}</p>
                      {style.fmt && (() => {
                        const eff = style.effectiveFormat(domain, t.name);
                        const kind = eff.key ? formatKind(eff.key, style.fmt!.formats?.[eff.key]) : 'none';
                        return (
                          <span className="ait-cat-tool__fmt" title={`답변 포맷: ${fmtLabelOf(style, eff.key)} (${eff.source})`}>
                            <span className="ait-cat-tool__fmt-mock"><FormatMock kind={kind} /></span>
                            <span className="ait-cat-tool__fmt-lbl">{fmtLabelOf(style, eff.key)}</span>
                            {eff.source !== '미설정' && <span className={`ait-cat-tool__fmt-src${eff.source === '개별' ? ' is-ov' : ''}`}>{eff.source}</span>}
                          </span>
                        );
                      })()}
                      <span className="ait-cat-tool__hint">상세 보기 →</span>
                    </button>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {selected && (
        <ToolDetailModal
          tool={selected.tool}
          accent={selected.accent}
          domainLabel={DOMAIN_LABELS[selected.tool.domain] ?? selected.tool.domain}
          domain={selected.domain}
          style={style}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

// 카테고리(도메인) 기본 답변 톤·포맷 선택 — synthesis-format.json style_by_tool["<domain>."].
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function CategoryStyleRow({ domain, style }: { domain: string; style: StyleApi }) {
  const st = style.styleForCategory(domain);
  const toneKeys = Object.keys(style.fmt?.tones ?? {});
  return (
    <div className="ait-cat-style">
      <span className="ait-cat-style__lbl">이 카테고리 기본 답변</span>
      <label className="ait-cat-style__fld">톤
        <select className="aiaf-select aiaf-select--sm" value={st.tone ?? ''}
          onChange={(e) => style.setCategoryTone(domain, e.target.value)}>
          <option value="">미설정</option>
          {toneKeys.map((k) => <option key={k} value={k}>{toneLabelOf(style, k)}</option>)}
        </select>
      </label>
      <span className="ait-cat-style__fld">포맷
        <FormatSelect value={st.format ?? ''} formats={style.fmt?.formats ?? {}}
          onChange={(v) => style.setCategoryFormat(domain, v)} placeholder="미설정" />
      </span>
      <button type="button" className={`ait-cat-style__save${style.categoryDirty(domain) ? ' is-dirty' : ''}`}
        disabled={style.savingCategory === domain || !style.categoryDirty(domain)}
        title="이 카테고리의 톤·포맷 매핑만 저장합니다"
        onClick={() => void style.saveCategory(domain)}>
        {style.savingCategory === domain ? '저장 중…' : (style.categoryDirty(domain) ? '이 카테고리 저장' : '저장됨')}
      </button>
    </div>
  );
}

// 도구별 답변 포맷 선택(아이콘 카드) — 선택 시 포맷 예시 자동 표시. 톤은 카테고리 단위만(여기선 포맷만).
function ToolFormatPicker({ tool, domain, style }: { tool: AiToolMeta; domain: string; style: StyleApi }) {
  const toolFmt = style.styleForTool(tool.name).format;
  const catFmt = style.styleForCategory(domain).format;
  const effKey = toolFmt ?? catFmt;
  const effEntry = effKey ? style.fmt?.formats?.[effKey] : undefined;
  const pal = effEntry?.palette;
  const source = toolFmt ? '개별' : (catFmt ? '카테고리 기본' : '미설정');
  return (
    <div className="ait-modal__fmt">
      <SecLabel icon={List}>답변 포맷 (이 도구) — 선택하면 예시가 표시됩니다</SecLabel>
      <div className="ait-modal__fmt-row">
        <FormatSelect value={toolFmt ?? ''} formats={style.fmt?.formats ?? {}}
          onChange={(v) => style.setToolFormat(tool.name, v)}
          placeholder={catFmt ? `카테고리 기본 따름 (${fmtLabelOf(style, catFmt)})` : '카테고리 기본 따름'} />
      </div>
      <div className="aiv-example" style={{ marginTop: 8, ...(formatStyleVars(effEntry) ?? {}) }}>
        <div className="aiv-example__bar">
          <span className="aiv-example__dot" />포맷 예시
          <span className="aiaf-preview__badge" style={{ marginLeft: 8 }}>{source}{effKey ? ` · ${fmtLabelOf(style, effKey)}` : ''}</span>
          {pal && pal.length > 0 && <span className="aiv-example__pal">{pal.slice(0, 6).map((c, i) => <i key={i} style={{ background: c }} />)}</span>}
        </div>
        <div className="aiv-example__body"><FormatSample kind={effKey ? formatKind(effKey, effEntry) : 'none'} /></div>
      </div>
      <p className="ait-modal__hint" style={{ marginTop: 6 }}>변경 후 화면 하단 “답변 톤·포맷 저장”으로 적용됩니다(최대 30초 내 채팅 반영).</p>
    </div>
  );
}

// 도구별 "주요 조회 결과 항목" — 도구 스펙에 출력 스키마가 없어, 검증된 대표 결과 필드를 큐레이션.
// [화면 라벨, 타입, 실제 필드명]. 실제 응답은 데이터에 따라 가변(대표 항목 안내용).
const RESULT_FIELDS: Record<string, Array<[string, string, string]>> = {
  'hr.me_profile': [
    ['사원번호', 'string', 'employee_number'], ['이름', 'string', 'name'], ['이메일', 'string', 'email'],
    ['전화', 'string', 'phone'], ['입사일', 'date', 'hire_date'], ['부서', 'string', 'department'], ['직책', 'string', 'position'],
  ],
  'hr.me_leave_balances': [
    ['휴가 종류', 'string', 'leave_type'], ['총 일수', 'number', 'total'], ['사용', 'number', 'used'], ['잔여', 'number', 'remaining'],
  ],
  'hr.me_leave_requests': [
    ['종류', 'string', 'type'], ['시작일', 'date', 'start_date'], ['종료일', 'date', 'end_date'], ['상태', 'string', 'status'], ['사유', 'string', 'reason'],
  ],
  'hr.me_attendance_summary': [
    ['기간', 'string', 'period'], ['근무일수', 'number', 'work_days'], ['근무시간', 'number', 'work_hours'], ['지각', 'number', 'late_count'], ['결근', 'number', 'absence_count'],
  ],
  'hr.me_organizations': [['조직', 'string', 'organization'], ['팀', 'string', 'team'], ['역할', 'string', 'role']],
  'calendar.list_events': [
    ['제목', 'string', 'title'], ['시작', 'datetime', 'startAt'], ['종료', 'datetime', 'endAt'], ['장소', 'string', 'location'], ['종일 여부', 'boolean', 'isAllDay'], ['유형', 'string', 'type'],
  ],
  'calendar.search_events': [
    ['제목', 'string', 'title'], ['시작', 'datetime', 'startAt'], ['종료', 'datetime', 'endAt'], ['장소', 'string', 'location'], ['종일 여부', 'boolean', 'isAllDay'], ['유형', 'string', 'type'],
  ],
  'calendar.create_event': [['일정 ID', 'string', 'id'], ['제목', 'string', 'title'], ['시작', 'datetime', 'startAt'], ['종료', 'datetime', 'endAt']],
  'calendar.update_event': [['일정 ID', 'string', 'id'], ['제목', 'string', 'title'], ['시작', 'datetime', 'startAt'], ['종료', 'datetime', 'endAt']],
  'calendar.delete_event': [['삭제 여부', 'boolean', 'deleted'], ['일정 ID', 'string', 'id']],
  'chat.recent_messages': [['작성자', 'string', 'sender'], ['내용', 'string', 'content'], ['시각', 'datetime', 'sentAt'], ['AI 여부', 'boolean', 'isAi']],
  'chat.search_messages': [['작성자', 'string', 'sender'], ['내용', 'string', 'content'], ['시각', 'datetime', 'sentAt'], ['AI 여부', 'boolean', 'isAi']],
};

// 섹션 라벨 — 아이콘(카테고리색) + 텍스트.
function SecLabel({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return <p className="ait-modal__field-label"><Icon size={13} className="ait-modal__field-icon" />{children}</p>;
}

// 도구 상세 레이어 팝업 — input_schema 파라미터·메타(sql/path/connection)·trust 등 노출.
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function ToolDetailModal({
  tool, accent, domainLabel, domain, style, onClose,
}: {
  tool: AiToolMeta;
  accent: { c: string; t: string };
  domainLabel: string;
  domain: string;
  /**
   * 답변 포맷 고르기를 이 팝업에 띄울지. **안 주면 안 띄운다** —
   * 「AI 도구(신)」은 톤·포맷을 제 화면에서 다루지 않는다(2026-09-09 사용자 지시).
   * 옛 화면은 지금까지처럼 그대로 넘기므로 동작이 안 바뀐다.
   */
  style?: StyleApi;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const meta = (tool._meta && typeof tool._meta === 'object' ? tool._meta : {}) as Record<string, unknown>;
  const kind = String(meta.tool_kind ?? tool.tool_kind ?? '');
  const sql = typeof meta.sql === 'string' ? meta.sql : '';
  const httpMethod = typeof meta.http_method === 'string' ? meta.http_method : '';
  const path = typeof meta.path === 'string' ? meta.path : '';
  const connectionId = typeof meta.connection_id === 'string' ? meta.connection_id : '';

  const props = tool.input_schema?.properties ?? {};
  const required = new Set(tool.input_schema?.required ?? []);
  const propEntries = Object.entries(props);

  // 동작 구분 — trust_class 기준(read=조회 / write=수정 / destructive=삭제·변경).
  const tc = tool.trust_class;
  const op = tc === 'destructive'
    ? { label: '삭제·변경', cls: 'danger', desc: '데이터를 삭제하거나 되돌리기 어려운 변경을 합니다' }
    : tc === 'write'
      ? { label: '수정(쓰기)', cls: 'write', desc: '데이터를 생성·수정합니다' }
      : { label: '조회(읽기)', cls: 'read', desc: '데이터를 조회만 합니다(변경 없음)' };
  const paramTitle = tc === 'read' ? '조회 조건 항목' : '수정·입력 항목';
  const resultFields = RESULT_FIELDS[tool.name] ?? null;

  return (
    <div className="ait-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="ait-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ait-modal-name"
        style={{ ['--cat' as string]: accent.c, ['--cat-tint' as string]: accent.t }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ait-modal__head">
          <span className="ait-modal__icon"><IconTool size={18} /></span>
          <div className="ait-modal__titles">
            <div className="ait-modal__name" id="ait-modal-name">{tool.name}</div>
            <div className="ait-modal__domain">{domainLabel} 도메인</div>
          </div>
          <button type="button" className="ait-modal__close" onClick={onClose} aria-label="닫기"><IconX /></button>
        </div>

        <div className="ait-modal__body">
          <div className="ait-modal__badges">
            {tool.required_role && <span className="ai-tools-badge">{tool.required_role}</span>}
            {tool.trust_class && <span className={`ai-tools-badge ai-tools-badge--${tool.trust_class}`}>{tool.trust_class}</span>}
            {tool.trust_level && <span className="ai-tools-badge">{tool.trust_level}</span>}
            {kind && <span className="ai-tools-badge ai-tools-badge--kind">{kind}</span>}
          </div>

          <div>
            <SecLabel icon={FileText}>설명</SecLabel>
            <p className="ait-modal__desc">{tool.description || '(설명 없음)'}</p>
          </div>

          {style?.fmt && <ToolFormatPicker tool={tool} domain={domain} style={style} />}

          {(tool.endpoint || path || httpMethod) && (
            <div>
              <SecLabel icon={Globe}>엔드포인트</SecLabel>
              <div className="ait-modal__mono">
                {httpMethod ? `${httpMethod} ` : ''}{path || tool.endpoint}
              </div>
            </div>
          )}

          {connectionId && (
            <div>
              <SecLabel icon={Link}>연결</SecLabel>
              <div className="ait-modal__mono">{connectionId}</div>
            </div>
          )}

          {/* 동작 구분 — 이 도구가 조회만 하는지 / 수정하는지 */}
          <div>
            <SecLabel icon={Zap}>동작 구분</SecLabel>
            <div className={`ait-op ait-op--${op.cls}`}>
              <span className="ait-op__tag">{op.label}</span>
              <span className="ait-op__desc">{op.desc}</span>
            </div>
          </div>

          {/* 조회/수정 항목 — 깔끔한 표 */}
          <div>
            <SecLabel icon={Filter}>{paramTitle} ({propEntries.length})</SecLabel>
            {propEntries.length === 0 ? (
              <p className="ait-modal__empty">항목이 없습니다.</p>
            ) : (
              <table className="ait-param-table">
                <thead>
                  <tr><th>항목</th><th>타입</th><th>필수</th><th>설명</th></tr>
                </thead>
                <tbody>
                  {propEntries.map(([pname, pdef]) => {
                    const d = (pdef && typeof pdef === 'object' ? pdef : {}) as Record<string, unknown>;
                    const ptype = typeof d.type === 'string' ? d.type : (Array.isArray(d.type) ? d.type.join(' | ') : '-');
                    const pdesc = typeof d.description === 'string' ? d.description : '';
                    const isReq = required.has(pname);
                    return (
                      <tr key={pname}>
                        <td><code className="ait-param-table__name">{pname}</code></td>
                        <td className="ait-param-table__type">{ptype || '-'}</td>
                        <td>{isReq ? <span className="ait-param-table__req">필수</span> : <span className="ait-param-table__opt">선택</span>}</td>
                        <td className="ait-param-table__desc">{pdesc || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* 조회 결과 항목 — 응답으로 돌아오는 주요 필드 */}
          <div>
            <SecLabel icon={Eye}>조회 결과 항목 {resultFields ? `(${resultFields.length})` : ''}</SecLabel>
            {resultFields ? (
              <>
                <table className="ait-param-table">
                  <thead>
                    <tr><th>항목</th><th>타입</th><th>필드명</th></tr>
                  </thead>
                  <tbody>
                    {resultFields.map(([label, rtype, rfield]) => (
                      <tr key={rfield}>
                        <td>{label}</td>
                        <td className="ait-param-table__type">{rtype}</td>
                        <td><code className="ait-param-table__name">{rfield}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="ait-modal__hint" style={{ marginTop: 6 }}>대표 항목입니다. 실제 응답은 데이터·조건에 따라 달라질 수 있습니다.</p>
              </>
            ) : (
              <p className="ait-modal__empty">정의된 결과 항목이 없습니다 — 실제 실행 결과로 확인하세요.</p>
            )}
          </div>

          {sql && (
            <div>
              <SecLabel icon={Code}>SQL</SecLabel>
              <pre className="ait-modal__pre">{sql}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  연결된 서버 카드 (상단 개요) — DB / 업무 서버(HTTP) 그룹별 카드 + 연결 테스트
// ════════════════════════════════════════════════════════════════

const DB_KIND_SET = new Set<AiToolConnectionKind>(['sqlite', 'postgres', 'mysql']);

// 도구 _meta.connection_id 기준 연결별 도구 수 집계.
function countToolsByConnection(tools: AiToolMeta[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const t of tools) {
    const cid = t._meta && typeof t._meta === 'object' ? (t._meta as Record<string, unknown>).connection_id : undefined;
    if (typeof cid === 'string' && cid) map[cid] = (map[cid] ?? 0) + 1;
  }
  return map;
}

// ─── 서버 카드 선택 → 해당 서버 도구만 필터 ───
export type ServerSel = { kind: 'connection'; id: string; label: string } | { kind: 'native'; domain: string; label: string } | null;
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function toolConnId(t: AiToolMeta): string {
  const m = (t._meta && typeof t._meta === 'object' ? t._meta : {}) as Record<string, unknown>;
  return typeof m.connection_id === 'string' ? m.connection_id : '';
}
function namePrefix(t: AiToolMeta): string { return t.name.includes('.') ? t.name.split('.')[0]! : 'static'; }
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function isNativeTool(t: AiToolMeta): boolean {
  if (toolConnId(t)) return false;
  const meta = (t._meta && typeof t._meta === 'object' ? t._meta : {}) as Record<string, unknown>;
  return !CUSTOM_KINDS.has(String(meta.tool_kind ?? t.tool_kind ?? ''));
}
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function filterToolsByServer(tools: AiToolMeta[], sel: ServerSel): AiToolMeta[] {
  if (!sel) return tools;
  if (sel.kind === 'connection') return tools.filter((t) => toolConnId(t) === sel.id);
  return tools.filter((t) => isNativeTool(t) && (t.domain === sel.domain || namePrefix(t) === sel.domain));
}
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function groupByDomain(tools: AiToolMeta[]): Record<string, AiToolMeta[]> {
  const m: Record<string, AiToolMeta[]> = {};
  for (const t of tools) { const d = t.domain || namePrefix(t); (m[d] ??= []).push(t); }
  return m;
}

// ─── 내장(native) 서버 — 코드 내장 도구 모듈. connections 스토어엔 없고 도구 카탈로그에만 노출. ───
// 도메인은 도구 이름 접두사(name.split('.')[0]) — orchestrator tools-admin 과 동일 규칙.
/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export const NATIVE_SERVERS: Record<string, { label: string; backing: string }> = {
  chat: { label: '채팅 (WorkChat)', backing: 'WorkChat Postgres 직접 읽기' },
  hr: { label: '인사 (HR)', backing: 'hr-test-svc + WorkChat DB' },
  calendar: { label: '캘린더', backing: 'WorkChat 읽기 · chat-server 쓰기' },
  wiki: { label: '위키 (통합 검색)', backing: 'chat-server 위키 검색 카탈로그' },
};

interface NativeServer { domain: string; label: string; backing: string; count: number }

// 내장(native) 도구를 도메인별로 묶어 서버 목록 도출.
// native = connection_id 없음 AND custom kind(external_http/external_db) 아님.
//   → chat/hr/calendar(*_internal), wiki(정적, tool_kind 없음) 포함, 사용자 추가 외부 도구는 제외.
function deriveNativeServers(tools: AiToolMeta[]): NativeServer[] {
  const counts: Record<string, number> = {};
  for (const t of tools) {
    const meta = (t._meta && typeof t._meta === 'object' ? t._meta : {}) as Record<string, unknown>;
    if (typeof meta.connection_id === 'string' && meta.connection_id) continue; // custom 외부 도구
    const kind = String(meta.tool_kind ?? t.tool_kind ?? '');
    if (CUSTOM_KINDS.has(kind)) continue; // external_http / external_db
    const domain = t.name.includes('.') ? t.name.split('.')[0]! : 'static';
    counts[domain] = (counts[domain] ?? 0) + 1;
  }
  return Object.entries(counts)
    .map(([domain, count]) => ({
      domain, count,
      label: NATIVE_SERVERS[domain]?.label ?? domain,
      backing: NATIVE_SERVERS[domain]?.backing ?? '오케스트레이터 내장 모듈',
    }))
    .sort((a, b) => a.domain.localeCompare(b.domain));
}

// 연결 상세 텍스트 + 부가 칩(스키마/SSL/인증).
function connCardDetail(c: AiToolConnection): string {
  if (c.kind === 'http') return c.base_url;
  if (c.kind === 'sqlite') return c.path;
  if (c.dsn_env) return `env: ${c.dsn_env}`;
  if (c.dsn) {
    // 평문 dsn 은 자격증명 마스킹 후 host 부만 노출.
    const m = /^[a-z]+:\/\/(?:[^@/]+@)?([^/?#]+)/i.exec(c.dsn);
    return m ? `${c.kind}://****@${m[1]}` : '(인라인 dsn)';
  }
  return '(미설정)';
}

function ServerCards({ connections, tools, selected, onSelect }: {
  connections: AiToolConnection[]; tools: AiToolMeta[];
  selected: ServerSel; onSelect: (s: ServerSel) => void;
}) {
  const counts = useMemo(() => countToolsByConnection(tools), [tools]);
  const natives = useMemo(() => deriveNativeServers(tools), [tools]);
  const dbConns = connections.filter((c) => DB_KIND_SET.has(c.kind));
  const httpConns = connections.filter((c) => c.kind === 'http');
  const selDomain = selected?.kind === 'native' ? selected.domain : null;
  const selConn = selected?.kind === 'connection' ? selected.id : null;
  const pickNative = (n: NativeServer) => onSelect(selDomain === n.domain ? null : { kind: 'native', domain: n.domain, label: n.label });
  const pickConn = (c: AiToolConnection) => onSelect(selConn === c.id ? null : { kind: 'connection', id: c.id, label: c.label || c.id });

  if (connections.length === 0 && natives.length === 0) {
    return <p className="ait-srv__empty">아직 연결된 서버가 없습니다. 아래 “도구 생성기”에서 DB·API 연결을 추가하세요.</p>;
  }

  return (
    <div className="ait-srv">
      {natives.length > 0 && (
        <section className="ait-srv-cat ait-srv-cat--native">
          <header className="ait-srv-cat__head">
            <span className="ait-srv-cat__icon"><IconServer size={17} /></span>
            <span className="ait-srv-cat__title">내장 서버</span>
            <span className="ait-srv-cat__count">{natives.length}</span>
            <span className="ait-srv-cat__desc">오케스트레이터 기본 내장 도구 서버</span>
          </header>
          <div className="ait-srv__grid">
            {natives.map((n) => <NativeCard key={n.domain} srv={n} selected={selDomain === n.domain} onSelect={() => pickNative(n)} />)}
          </div>
        </section>
      )}

      <section className="ait-srv-cat ait-srv-cat--db">
        <header className="ait-srv-cat__head">
          <span className="ait-srv-cat__icon"><IconDbLarge size={17} /></span>
          <span className="ait-srv-cat__title">데이터베이스</span>
          <span className="ait-srv-cat__count">{dbConns.length}</span>
          <span className="ait-srv-cat__desc">사용자가 추가한 DB 연결 (sqlite/postgres/mysql)</span>
        </header>
        {dbConns.length === 0 ? (
          <p className="ait-srv__empty">연결된 데이터베이스가 없습니다.</p>
        ) : (
          <div className="ait-srv__grid">
            {dbConns.map((c) => <ConnCard key={c.id} conn={c} toolCount={counts[c.id] ?? 0} selected={selConn === c.id} onSelect={() => pickConn(c)} />)}
          </div>
        )}
      </section>

      <section className="ait-srv-cat ait-srv-cat--http">
        <header className="ait-srv-cat__head">
          <span className="ait-srv-cat__icon"><IconServer size={17} /></span>
          <span className="ait-srv-cat__title">업무 서버 (API)</span>
          <span className="ait-srv-cat__count">{httpConns.length}</span>
          <span className="ait-srv-cat__desc">사용자가 추가한 외부 HTTP/API 연결</span>
        </header>
        {httpConns.length === 0 ? (
          <p className="ait-srv__empty">연결된 업무 서버가 없습니다.</p>
        ) : (
          <div className="ait-srv__grid">
            {httpConns.map((c) => <ConnCard key={c.id} conn={c} toolCount={counts[c.id] ?? 0} selected={selConn === c.id} onSelect={() => pickConn(c)} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function ConnCard({ conn, toolCount, selected, onSelect }: { conn: AiToolConnection; toolCount: number; selected: boolean; onSelect: () => void }) {
  const [test, setTest] = useState<ConnectionTestResult | 'loading' | null>(null);
  const isDb = DB_KIND_SET.has(conn.kind);

  const runTest = async () => {
    setTest('loading');
    try {
      setTest(await testAiToolConnection(conn.id));
    } catch (e) {
      setTest({ ok: false, error: errMsg(e) });
    }
  };

  const dotClass = test === 'loading' ? 'ait-srv-dot--loading'
    : test == null ? ''
    : test.ok ? 'ait-srv-dot--ok' : 'ait-srv-dot--bad';
  const statusText = test === 'loading' ? '테스트 중…'
    : test == null ? '미확인'
    : test.ok ? `정상 ${test.latency_ms != null ? `· ${test.latency_ms}ms` : ''}` : '연결 실패';

  return (
    <div className={`ait-srv-card ait-srv-card--clickable${selected ? ' is-sel' : ''}`}
      role="button" tabIndex={0} aria-pressed={selected}
      title={selected ? '클릭하면 전체 도구 보기' : '클릭하면 이 서버의 도구만 보기'}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}>
      <div className="ait-srv-card__top">
        <span className="ait-srv-card__icon">{isDb ? <IconDbLarge /> : <IconServer />}</span>
        <span className="ait-srv-card__heading">
          <span className="ait-srv-card__name" title={conn.label || conn.id}>{conn.label || conn.id}</span>
          {conn.label && <span className="ait-srv-card__id">{conn.id}</span>}
        </span>
        <span className="ait-srv-card__kind">{conn.kind}</span>
      </div>

      <div className="ait-srv-card__detail">{connCardDetail(conn)}</div>

      <div className="ait-srv-card__chips">
        <span className="ait-srv-card__chip">도구 {toolCount}개</span>
        {conn.kind === 'postgres' && conn.schema && <span className="ait-srv-card__chip">schema: {conn.schema}</span>}
        {(conn.kind === 'postgres' || conn.kind === 'mysql') && conn.ssl && <span className="ait-srv-card__chip">SSL</span>}
        {conn.kind === 'http' && conn.auth && conn.auth.type !== 'none' && <span className="ait-srv-card__chip">auth: {conn.auth.type}</span>}
      </div>

      {test && test !== 'loading' && !test.ok && test.error && (
        <p className="ait-srv-card__err">{test.error}</p>
      )}

      <div className="ait-srv-card__foot">
        <span className="ait-srv-card__status">
          <span className={`ait-srv-dot ${dotClass}`} />
          {statusText}
        </span>
        <button className="ait-srv-card__test" disabled={test === 'loading'} onClick={(e) => { e.stopPropagation(); void runTest(); }}>
          {test === 'loading' ? '확인 중…' : '연결 테스트'}
        </button>
      </div>
    </div>
  );
}

// 내장 서버 카드 — 코드 내장 모듈이라 연결 테스트/삭제 없음(읽기 전용 개요). 도메인·연결대상·도구 수.
function NativeCard({ srv, selected, onSelect }: { srv: NativeServer; selected: boolean; onSelect: () => void }) {
  const isDb = srv.domain === 'chat' || srv.domain === 'calendar';
  return (
    <div className={`ait-srv-card ait-srv-card--clickable${selected ? ' is-sel' : ''}`}
      role="button" tabIndex={0} aria-pressed={selected}
      title={selected ? '클릭하면 전체 도구 보기' : '클릭하면 이 서버의 도구만 보기'}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}>
      <div className="ait-srv-card__top">
        <span className="ait-srv-card__icon">{isDb ? <IconDbLarge /> : <IconServer />}</span>
        <span className="ait-srv-card__heading">
          <span className="ait-srv-card__name" title={srv.label}>{srv.label}</span>
          <span className="ait-srv-card__id">{srv.domain}.*</span>
        </span>
        <span className="ait-srv-card__kind">내장</span>
      </div>

      <div className="ait-srv-card__detail">{srv.backing}</div>

      <div className="ait-srv-card__chips">
        <span className="ait-srv-card__chip">도구 {srv.count}개</span>
      </div>

      <div className="ait-srv-card__foot">
        <span className="ait-srv-card__status">
          <span className="ait-srv-dot ait-srv-dot--ok" />
          활성 (내장)
        </span>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  연결 선택기 (드롭다운 + 새 연결 토글 + test/삭제) — DB·brief 모드 공용
// ════════════════════════════════════════════════════════════════

interface NewConn {
  id: string;
  kind: AiToolConnectionKind;
  label: string;
  base_url: string;
  path: string;
  dsn_env: string;
  dsn: string;
  ssl: boolean;
  schema: string;
}
function emptyConn(kind: AiToolConnectionKind): NewConn {
  return { id: '', kind, label: '', base_url: '', path: '', dsn_env: '', dsn: '', ssl: false, schema: '' };
}
function buildConnBody(c: NewConn): AiToolConnection {
  const id = c.id.trim();
  const label = c.label.trim() || undefined;
  if (c.kind === 'http') return { id, kind: 'http', label, base_url: c.base_url.trim() };
  if (c.kind === 'sqlite') return { id, kind: 'sqlite', label, path: c.path.trim() };
  return {
    id, kind: c.kind, label,
    ...(c.dsn_env.trim() ? { dsn_env: c.dsn_env.trim() } : {}),
    ...(c.dsn.trim() ? { dsn: c.dsn.trim() } : {}),
    ...(c.ssl ? { ssl: true } : {}),
    ...(c.kind === 'postgres' && c.schema.trim() ? { schema: c.schema.trim() } : {}),
  };
}
function connCanAdd(c: NewConn): boolean {
  if (!c.id.trim()) return false;
  if (c.kind === 'http') return !!c.base_url.trim();
  if (c.kind === 'sqlite') return !!c.path.trim();
  return !!(c.dsn_env.trim() || c.dsn.trim());
}
function connDetail(c: AiToolConnection): string {
  if (c.kind === 'http') return c.base_url;
  if (c.kind === 'sqlite') return c.path;
  return c.dsn_env ? `env:${c.dsn_env}` : (c.dsn ?? '(dsn)');
}

function ConnectionPicker({
  label, connections, allowedKinds, value, onChange, onChanged,
}: {
  label: string;
  connections: AiToolConnection[];
  allowedKinds: AiToolConnectionKind[];
  value: string;
  onChange: (id: string) => void;
  onChanged: () => Promise<void>;
}) {
  const [adding, setAdding] = useState(connections.length === 0);
  const [form, setForm] = useState<NewConn>(() => emptyConn(allowedKinds[0]!));
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [test, setTest] = useState<ConnectionTestResult | 'loading' | null>(null);

  const onAdd = async () => {
    setBusy('add'); setErr(null);
    try {
      const body = buildConnBody(form);
      await createAiToolConnection(body);
      onChange(body.id);
      setForm(emptyConn(allowedKinds[0]!));
      setAdding(false);
      await onChanged();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const onTest = async () => {
    if (!value) return;
    setTest('loading');
    try {
      setTest(await testAiToolConnection(value));
    } catch (e) {
      setTest({ ok: false, error: errMsg(e) });
    }
  };

  const onDelete = async () => {
    if (!value) return;
    if (!window.confirm(`연결 "${value}" 및 이 연결을 쓰는 도구를 삭제할까요?`)) return;
    setBusy('del'); setErr(null);
    try {
      await deleteAiToolConnection(value);
      onChange('');
      setTest(null);
      await onChanged();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="ai-tools-connpick">
      <div className="ai-tools-connpick__row">
        <label className="settings-field" style={{ flex: 1 }}>
          <span>{label}</span>
          <StyledSelect
            value={value}
            placeholder="연결 선택…"
            onChange={(v) => { onChange(v); setTest(null); }}
            options={[
              { value: '', label: '연결 선택…' },
              ...connections.map((c) => ({ value: c.id, label: `${c.label ? `${c.label} (${c.id})` : c.id} · ${c.kind}` })),
            ]}
          />
        </label>
        <button className={`settings-btn ${adding ? 'is-active' : ''}`} onClick={() => setAdding((a) => !a)}>
          <IconPlus /> 새 연결
        </button>
        <button className="settings-btn" disabled={!value || test === 'loading'} onClick={() => void onTest()}>
          {test === 'loading' ? '테스트 중…' : '테스트'}
        </button>
        <button className="settings-btn settings-btn--danger" disabled={!value || busy === 'del'} onClick={() => void onDelete()}>
          삭제
        </button>
      </div>

      {test && test !== 'loading' && (
        <p className={test.ok ? 'settings-muted' : 'settings-error'}>
          {test.ok
            ? `OK ${test.status ?? ''} ${test.latency_ms ?? ''}ms ${test.tables ? `tables: ${test.tables.join(', ')}` : ''}`
            : `실패: ${test.error}`}
        </p>
      )}
      {value && !test && (() => {
        const c = connections.find((x) => x.id === value);
        return c ? <p className="settings-muted">{c.kind} · {connDetail(c)}</p> : null;
      })()}
      {err && <div className="settings-error" role="alert">{err}</div>}

      {adding && (
        <div className="ai-tools-conn-form ai-tools-conn-form--inline">
          <h4>새 연결 추가</h4>
          <div className="ai-tools-conn-form__row">
            <input placeholder="id (영문)" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} />
            <StyledSelect
              value={form.kind}
              onChange={(v) => setForm({ ...emptyConn(v as AiToolConnectionKind), id: form.id, label: form.label })}
              options={allowedKinds.map((k) => ({ value: k, label: k }))}
            />
            <input placeholder="label (선택)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </div>

          {form.kind === 'http' && (
            <input className="ai-tools-conn-form__wide" placeholder="base_url (https://...)" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} />
          )}
          {form.kind === 'sqlite' && (
            <input className="ai-tools-conn-form__wide" placeholder="sqlite 절대 경로 (/...)" value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} />
          )}
          {(form.kind === 'postgres' || form.kind === 'mysql') && (
            <>
              <input className="ai-tools-conn-form__wide" placeholder="dsn_env — DSN 담은 환경변수명 (권장, 예: MY_DB_DSN)" value={form.dsn_env} onChange={(e) => setForm({ ...form, dsn_env: e.target.value })} />
              <input className="ai-tools-conn-form__wide" placeholder={`인라인 dsn (${form.kind === 'postgres' ? 'postgres://user:pw@host:5432/db' : 'mysql://user:pw@host:3306/db'}) — 평문 저장`} value={form.dsn} onChange={(e) => setForm({ ...form, dsn: e.target.value })} />
              <div className="ai-tools-conn-form__row">
                <label className="settings-checkbox"><input type="checkbox" checked={form.ssl} onChange={(e) => setForm({ ...form, ssl: e.target.checked })} /> SSL</label>
                {form.kind === 'postgres' && (
                  <input placeholder="schema (기본 public)" value={form.schema} onChange={(e) => setForm({ ...form, schema: e.target.value })} />
                )}
              </div>
              {form.dsn.trim() && !form.dsn_env.trim() && (
                <p className="settings-muted">인라인 dsn 은 디스크에 평문 저장됩니다. 운영에서는 dsn_env 사용을 권장합니다.</p>
              )}
            </>
          )}

          <div className="ai-tools-preview__actions">
            <button className="settings-btn settings-btn--primary" disabled={busy === 'add' || !connCanAdd(form)} onClick={() => void onAdd()}>
              {busy === 'add' ? '추가 중…' : '연결 추가'}
            </button>
            <button className="settings-btn" onClick={() => setAdding(false)}>취소</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  모드 a. DB 분석 생성
// ════════════════════════════════════════════════════════════════

/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function DbGenerator({ connections, reload }: { connections: AiToolConnection[]; reload: () => Promise<void> }) {
  const dbConnections = useMemo(() => connections.filter((c) => DB_KINDS.includes(c.kind)), [connections]);
  const [connId, setConnId] = useState('');
  const [schema, setSchema] = useState<DbSchema | null>(null);
  const [schemaBusy, setSchemaBusy] = useState(false);
  const [schemaErr, setSchemaErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [focus, setFocus] = useState('');
  const [maxTools, setMaxTools] = useState(12);
  const [cands, setCands] = useState<ToolCandidate[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [genBusy, setGenBusy] = useState(false);
  const [genErr, setGenErr] = useState<string | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  const pickConn = (id: string) => { setConnId(id); setSchema(null); setCands(null); };

  const analyze = async () => {
    if (!connId) return;
    setSchemaBusy(true); setSchemaErr(null); setSchema(null); setCands(null);
    try {
      const s = await introspectConnection(connId);
      setSchema(s);
      setExpanded(new Set(s.tables.slice(0, 3).map((t) => t.name)));
    } catch (e) {
      setSchemaErr(errMsg(e));
    } finally {
      setSchemaBusy(false);
    }
  };

  const generate = async () => {
    if (!connId) return;
    setGenBusy(true); setGenErr(null); setCands(null); setSaveMsg(null);
    try {
      const r = await synthesizeBatch(connId, { focus: focus.trim() || undefined, maxTools });
      setCands(r.candidates);
      setSelected(new Set(r.candidates.map((c, i) => (c.validated && !c.duplicate ? i : -1)).filter((i) => i >= 0)));
    } catch (e) {
      setGenErr(errMsg(e));
    } finally {
      setGenBusy(false);
    }
  };

  const editCand = (i: number, patch: Partial<AiToolMeta>) => {
    setCands((prev) => prev && prev.map((c, idx) => (idx === i ? { ...c, proposed: { ...c.proposed, ...patch } } : c)));
  };

  const saveSelected = async () => {
    if (!cands) return;
    const tools = [...selected].map((i) => cands[i]?.proposed).filter(Boolean) as AiToolMeta[];
    if (tools.length === 0) return;
    setSaveBusy(true); setSaveMsg(null); setGenErr(null);
    try {
      const r = await createCustomAiToolsBatch(tools);
      setSaveMsg(`저장 ${r.added.length}개${r.skipped.length ? ` · 건너뜀 ${r.skipped.length}개 (${r.skipped.map((s) => `${s.name}:${s.reason}`).join(', ')})` : ''}`);
      setCands(null);
      setSelected(new Set());
      await reload();
    } catch (e) {
      setGenErr(errMsg(e));
    } finally {
      setSaveBusy(false);
    }
  };

  return (
    <div className="ai-tools-gen">
      <ConnectionPicker
        label="DB 연결 (sqlite / postgres / mysql)"
        connections={dbConnections}
        allowedKinds={DB_KINDS}
        value={connId}
        onChange={pickConn}
        onChanged={reload}
      />
      <div className="ai-tools-gen__row">
        <button className="settings-btn settings-btn--primary" disabled={!connId || schemaBusy} onClick={() => void analyze()}>
          {schemaBusy ? '분석 중…' : '스키마 분석'}
        </button>
      </div>
      {schemaErr && <div className="settings-error" role="alert">{schemaErr}</div>}

      {schema && (
        <>
          <div className="ai-tools-schema">
            <div className="ai-tools-schema__head">
              스키마: <strong>{schema.engine}</strong>{schema.schema ? ` / ${schema.schema}` : ''} · 테이블 {schema.table_count}개
              {schema.truncated && <span className="settings-muted"> (상위 {schema.tables.length}개만 표시)</span>}
            </div>
            <ul className="ai-tools-schema__tables">
              {schema.tables.map((t) => {
                const open = expanded.has(t.name);
                return (
                  <li key={t.name} className="ai-tools-schema__table">
                    <button className="ai-tools-schema__toggle" onClick={() => setExpanded((p) => {
                      const n = new Set(p); n.has(t.name) ? n.delete(t.name) : n.add(t.name); return n;
                    })}>
                      <IconChevron open={open} />
                      <code>{t.name}</code>
                      <span className="settings-muted">
                        {t.columns.length} cols{t.approx_rows != null ? ` · ~${t.approx_rows} rows` : ''}
                        {t.foreign_keys.length ? ` · ${t.foreign_keys.length} FK` : ''}
                      </span>
                    </button>
                    {open && (
                      <div className="ai-tools-schema__cols">
                        {t.columns.map((c) => (
                          <span key={c.name} className={`ai-tools-col ${c.pk ? 'is-pk' : ''}`}>
                            {c.name}<span className="ai-tools-col__type">{c.type}</span>
                            {c.pk && <span className="ai-tools-badge ai-tools-badge--kind">PK</span>}
                            {!c.nullable && <span className="ai-tools-col__nn">NN</span>}
                          </span>
                        ))}
                        {t.foreign_keys.map((fk) => (
                          <span key={fk.column} className="ai-tools-col ai-tools-col--fk">
                            FK {fk.column} → {fk.ref_table}.{fk.ref_column}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="ai-tools-gen__row">
            <label className="settings-field" style={{ flex: 1 }}>
              <span>생성 초점 (선택)</span>
              <input value={focus} maxLength={2000} placeholder="예) 주문/고객 조회 위주, 매출 집계 포함" onChange={(e) => setFocus(e.target.value)} />
            </label>
            <label className="settings-field" style={{ width: 110 }}>
              <span>최대 개수</span>
              <input type="number" min={1} max={25} value={maxTools} onChange={(e) => setMaxTools(Math.min(25, Math.max(1, Number(e.target.value) || 12)))} />
            </label>
            <button className="settings-btn settings-btn--primary" disabled={genBusy} onClick={() => void generate()}>
              {genBusy ? 'AI 생성 중…' : 'AI 로 도구 생성'}
            </button>
          </div>
        </>
      )}

      {genErr && <div className="settings-error" role="alert">{genErr}</div>}
      {saveMsg && <div className="settings-note" role="status">{saveMsg}</div>}

      {cands && (
        <div className="ai-tools-cands">
          <div className="ai-tools-cands__head">
            <strong>생성 후보 {cands.length}개</strong>
            <span className="settings-muted"> · 선택 {selected.size}개</span>
            <button className="settings-btn settings-btn--primary" disabled={saveBusy || selected.size === 0} onClick={() => void saveSelected()}>
              {saveBusy ? '저장 중…' : `선택 ${selected.size}개 저장`}
            </button>
          </div>
          <ul className="ai-tools-cands__list">
            {cands.map((c, i) => (
              <CandidateCard
                key={i}
                cand={c}
                checked={selected.has(i)}
                onToggle={() => setSelected((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; })}
                onEdit={(patch) => editCand(i, patch)}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CandidateCard({
  cand, checked, onToggle, onEdit,
}: {
  cand: ToolCandidate;
  checked: boolean;
  onToggle: () => void;
  onEdit: (patch: Partial<AiToolMeta>) => void;
}) {
  const [open, setOpen] = useState(false);
  const sql = String(cand.proposed._meta?.sql ?? '');
  const path = String(cand.proposed._meta?.path ?? '');
  return (
    <li className={`ai-tools-cand ${cand.validated ? '' : 'is-invalid'}`}>
      <div className="ai-tools-cand__top">
        <input type="checkbox" checked={checked} disabled={!cand.validated} onChange={onToggle} />
        <input
          className="ai-tools-cand__name"
          value={cand.proposed.name}
          onChange={(e) => onEdit({ name: e.target.value })}
        />
        <div className="ai-tools-cand__badges">
          {cand.validated
            ? <span className="ai-tools-badge ai-tools-badge--read">검증됨</span>
            : <span className="ai-tools-badge ai-tools-badge--destructive">오류</span>}
          {cand.duplicate && <span className="ai-tools-badge">중복</span>}
          <button className="ai-tools-cand__more" onClick={() => setOpen((o) => !o)}><IconChevron open={open} /> 상세</button>
        </div>
      </div>
      <textarea
        className="ai-tools-cand__desc"
        rows={2}
        value={cand.proposed.description}
        onChange={(e) => onEdit({ description: e.target.value })}
      />
      {!cand.validated && cand.validation_error && (
        <p className="settings-error">{cand.validation_error}</p>
      )}
      {open && (
        <div className="ai-tools-cand__detail">
          {sql && <pre className="ai-tools-preview__json">{sql}</pre>}
          {path && <code className="ai-tools-item__name">{String(cand.proposed._meta?.http_method ?? 'GET')} {path}</code>}
          <pre className="ai-tools-preview__json">{JSON.stringify(cand.proposed.input_schema, null, 2)}</pre>
        </div>
      )}
    </li>
  );
}

// ════════════════════════════════════════════════════════════════
//  모드 b. Swagger(OpenAPI) 가져오기
// ════════════════════════════════════════════════════════════════

type DraftView = OpenApiPreview['tools'][number] & { recommended?: boolean };

/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function SwaggerImporter({ onSaved }: { onSaved: () => Promise<void> }) {
  const [connId, setConnId] = useState('');
  const [sourceType, setSourceType] = useState<'url' | 'spec'>('url');
  const [url, setUrl] = useState('');
  const [spec, setSpec] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [label, setLabel] = useState('');

  const [preview, setPreview] = useState<OpenApiPreview | null>(null);
  const [drafts, setDrafts] = useState<DraftView[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const [authType, setAuthType] = useState<'none' | 'bearer' | 'header' | 'query'>('none');
  const [authKey, setAuthKey] = useState('');
  const [authValueEnv, setAuthValueEnv] = useState('');

  const doPreview = async () => {
    if (!connId.trim()) { setErr('connection_id 를 입력하세요'); return; }
    setBusy('preview'); setErr(null); setMsg(null); setPreview(null);
    try {
      const p = await openapiPreview({
        connectionId: connId.trim(),
        url: sourceType === 'url' ? url.trim() : undefined,
        spec: sourceType === 'spec' ? spec : undefined,
        baseUrlOverride: baseUrl.trim() || undefined,
        labelOverride: label.trim() || undefined,
      });
      setPreview(p);
      setDrafts(p.tools);
      setSelected(new Set(p.tools.filter((t) => t.enabled_default).map((t) => t.draft_id)));
      if (p.connection_draft?.base_url) setBaseUrl(p.connection_draft.base_url);
      if (p.connection_draft?.auth) {
        setAuthType((p.connection_draft.auth.type as typeof authType) ?? 'none');
        setAuthKey(p.connection_draft.auth.key ?? '');
        setAuthValueEnv(p.connection_draft.auth.value_env ?? '');
      }
      if (p.error) setErr(p.error);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const enhance = async () => {
    if (drafts.length === 0) return;
    setBusy('enhance'); setErr(null);
    try {
      const r = await openapiSynthesize(
        drafts.map((d) => ({ name: d.spec.name, method: d.method, path: d.path, summary: d.spec.description })),
      );
      setDrafts((prev) => prev.map((d) => {
        const e = r.enhancements[d.spec.name];
        if (!e) return d;
        return {
          ...d,
          recommended: e.recommended,
          spec: { ...d.spec, description: e.description ?? d.spec.description },
        };
      }));
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const doImport = async () => {
    const chosen = drafts.filter((d) => selected.has(d.draft_id));
    if (chosen.length === 0) { setErr('가져올 도구를 선택하세요'); return; }
    if (!baseUrl.trim()) { setErr('base_url 을 입력하세요'); return; }
    setBusy('import'); setErr(null); setMsg(null);
    try {
      const auth = authType === 'none' ? { type: 'none' as const }
        : { type: authType, ...(authKey ? { key: authKey } : {}), ...(authValueEnv ? { value_env: authValueEnv } : {}) };
      const r = await openapiImport({
        connectionId: connId.trim(),
        baseUrl: baseUrl.trim(),
        label: label.trim() || undefined,
        auth,
        selectedTools: chosen.map((d) => d.spec),
      });
      setMsg(`연결 1개 + 도구 ${r.added.tools}개 가져옴`);
      setPreview(null); setDrafts([]); setSelected(new Set());
      await onSaved();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="ai-tools-gen">
      <p className="settings-muted">OpenAPI 3.x 명세를 주면 연결과 도구가 함께 생성됩니다 (별도 연결 추가 불필요).</p>
      <div className="ai-tools-gen__row">
        <label className="settings-field" style={{ width: 200 }}>
          <span>connection id (신규)</span>
          <input value={connId} placeholder="예: petstore" onChange={(e) => setConnId(e.target.value)} />
        </label>
        <label className="settings-field" style={{ width: 130 }}>
          <span>명세 입력</span>
          <StyledSelect value={sourceType} onChange={(v) => setSourceType(v as 'url' | 'spec')}
            options={[{ value: 'url', label: 'URL' }, { value: 'spec', label: '본문 붙여넣기' }]} />
        </label>
        <label className="settings-field" style={{ flex: 1 }}>
          <span>label (선택)</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>
      </div>
      {sourceType === 'url' ? (
        <input className="ai-tools-conn-form__wide" placeholder="OpenAPI 3.x JSON/YAML URL (https://...)" value={url} onChange={(e) => setUrl(e.target.value)} />
      ) : (
        <textarea className="ai-tools-conn-form__wide" rows={5} placeholder="OpenAPI 3.x 명세 본문(JSON/YAML) 붙여넣기" value={spec} onChange={(e) => setSpec(e.target.value)} />
      )}
      <input className="ai-tools-conn-form__wide" placeholder="base_url override (선택 — 명세 servers 가 없거나 내부 host 일 때)" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
      <button className="settings-btn settings-btn--primary" disabled={busy === 'preview' || !connId.trim()} onClick={() => void doPreview()}>
        {busy === 'preview' ? '미리보기 중…' : '미리보기'}
      </button>

      {err && <div className="settings-error" role="alert">{err}</div>}
      {msg && <div className="settings-note" role="status">{msg}</div>}

      {preview && drafts.length > 0 && (
        <>
          <div className="ai-tools-cands__head">
            <strong>엔드포인트 {preview.stats.total}개</strong>
            <span className="settings-muted"> · GET 자동선택 {preview.stats.auto_enabled}개 · 선택 {selected.size}개</span>
            <button className="settings-btn" disabled={busy === 'enhance'} onClick={() => void enhance()}>
              {busy === 'enhance' ? 'AI 보강 중…' : 'AI 설명 보강'}
            </button>
          </div>
          {preview.warnings.length > 0 && (
            <details className="ai-tools-warnings"><summary>경고 {preview.warnings.length}개</summary>
              <ul>{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </details>
          )}
          <ul className="ai-tools-cands__list">
            {drafts.map((d) => (
              <li key={d.draft_id} className="ai-tools-cand">
                <div className="ai-tools-cand__top">
                  <input type="checkbox" checked={selected.has(d.draft_id)} onChange={() => setSelected((p) => {
                    const n = new Set(p); n.has(d.draft_id) ? n.delete(d.draft_id) : n.add(d.draft_id); return n;
                  })} />
                  <code className="ai-tools-item__name">{d.spec.name}</code>
                  <div className="ai-tools-cand__badges">
                    <span className={`ai-tools-badge ai-tools-badge--${d.method === 'get' ? 'read' : 'kind'}`}>{d.method.toUpperCase()}</span>
                    {d.recommended === true && <span className="ai-tools-badge ai-tools-badge--read">추천</span>}
                    {d.recommended === false && <span className="ai-tools-badge">비추천</span>}
                  </div>
                </div>
                <p className="ai-tools-item__desc"><code>{d.path}</code> — {d.spec.description}</p>
                {d.disabled_reason && <p className="settings-muted">{d.disabled_reason}</p>}
              </li>
            ))}
          </ul>

          <div className="ai-tools-conn-form">
            <h4>인증 (선택)</h4>
            <div className="ai-tools-conn-form__row">
              <StyledSelect value={authType} onChange={(v) => setAuthType(v as typeof authType)}
                options={[{ value: 'none', label: 'none' }, { value: 'bearer', label: 'bearer' }, { value: 'header', label: 'header' }, { value: 'query', label: 'query' }]} />
              {(authType === 'header' || authType === 'query') && (
                <input placeholder="key (예: X-API-Key)" value={authKey} onChange={(e) => setAuthKey(e.target.value)} />
              )}
              {authType !== 'none' && (
                <input placeholder="value_env (환경변수명, UPPER_SNAKE)" value={authValueEnv} onChange={(e) => setAuthValueEnv(e.target.value)} />
              )}
            </div>
            <button className="settings-btn settings-btn--primary" disabled={busy === 'import' || selected.size === 0} onClick={() => void doImport()}>
              {busy === 'import' ? '가져오는 중…' : `선택 ${selected.size}개 가져오기`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  모드 c. 수동 brief (단건 생성)
// ════════════════════════════════════════════════════════════════

/** ⚠ 2026-09-09 — 새 화면 `AiToolsListSection`(AI 도구(신))이 그대로 가져다 쓴다. 두 벌로 만들지 않는다. */
export function BriefGenerator({ connections, reload }: { connections: AiToolConnection[]; reload: () => Promise<void> }) {
  const [connId, setConnId] = useState('');
  const [brief, setBrief] = useState('');
  const [synth, setSynth] = useState<SynthesizeResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const onSynthesize = async () => {
    if (!brief.trim() || !connId) return;
    setBusy('synth'); setSynth(null); setErr(null);
    try {
      setSynth(await synthesizeAiTool(brief.trim(), connId));
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const onSave = async () => {
    if (!synth?.proposed) return;
    setBusy('save');
    try {
      await createCustomAiTool(synth.proposed);
      setSynth(null); setBrief('');
      await reload();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="ai-tools-gen">
      <ConnectionPicker
        label="연결 (http / sqlite / postgres / mysql)"
        connections={connections}
        allowedKinds={ALL_KINDS}
        value={connId}
        onChange={setConnId}
        onChanged={reload}
      />
      <label className="settings-field">
        <span>도구 설명 (brief)</span>
        <textarea rows={3} value={brief} maxLength={2000} placeholder="예) 도시 이름을 받아 현재 날씨를 조회한다" onChange={(e) => setBrief(e.target.value)} />
      </label>
      <button className="settings-btn settings-btn--primary" disabled={busy === 'synth' || !brief.trim() || !connId} onClick={() => void onSynthesize()}>
        {busy === 'synth' ? '생성 중…' : 'AI 로 생성'}
      </button>
      {err && <div className="settings-error" role="alert">{err}</div>}
      {synth?.proposed && (
        <div className="ai-tools-preview">
          <div className="ai-tools-preview__meta">
            <strong>{synth.proposed.name}</strong>
            {synth.model && <span className="settings-muted"> · {synth.model} · {synth.latency_ms}ms</span>}
            {synth.validated && <span className="ai-tools-badge ai-tools-badge--read"> 검증됨</span>}
          </div>
          <pre className="ai-tools-preview__json">{JSON.stringify(synth.proposed, null, 2)}</pre>
          {synth.rationale && <p className="settings-muted">{synth.rationale}</p>}
          <div className="ai-tools-preview__actions">
            <button className="settings-btn settings-btn--primary" disabled={busy === 'save'} onClick={() => void onSave()}>
              {busy === 'save' ? '저장 중…' : '이 도구 저장'}
            </button>
            <button className="settings-btn" onClick={() => setSynth(null)}>취소</button>
          </div>
        </div>
      )}
    </div>
  );
}
